import { Link } from 'react-router-dom'
import type { UseQueryResult } from '@tanstack/react-query'
import { useDashboard } from '../hooks/useDashboard'

interface CountTileSpec {
	label: string
	href: string
	query: UseQueryResult<number, Error>
	tone: string
}

function CountTile({ tile }: { tile: CountTileSpec }) {
	const { label, href, query, tone } = tile
	return (
		<Link
			to={href}
			className={`${tone} block rounded-lg p-5 transition-all hover:ring-2 hover:ring-offset-1 hover:ring-gray-300`}
		>
			<h3 className="text-sm font-semibold text-foreground">{label}</h3>
			<div className="mt-1 flex h-9 items-center">
				{query.isLoading || query.isPending ? (
					<div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-40" />
				) : (
					<p className="text-3xl font-bold">{query.data ?? '—'}</p>
				)}
			</div>
		</Link>
	)
}

function TileGroup({
	headingId,
	heading,
	tiles,
}: {
	headingId: string
	heading: React.ReactNode
	tiles: CountTileSpec[]
}) {
	return (
		<section aria-labelledby={headingId}>
			<h2 id={headingId} className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
				{heading}
			</h2>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				{tiles.map((tile) => (
					<CountTile key={tile.label} tile={tile} />
				))}
			</div>
		</section>
	)
}

/**
 * Admin-only cross-module count tiles. Three labelled groups:
 *
 *   People             → Employees, Contractors, Entra Directory
 *   Organization       → Clients, Projects, Providers
 *   Access & Security  → IDM Requests, ACC Mappings, Permissions
 *
 * Each tile is a click-through `<Link>` to the matching listing page.
 * Self-gates on `isSuperAdminViewer` (ADR-0009 admin-RC-only split); the
 * underlying queries gate via `enabled: admin` so non-admin USERs never
 * trigger any of these cross-module fetches.
 *
 * Spec 4 / Group 9f — `request-center-dashboard.md`. Carries over from
 * the legacy `/` admin DashboardPage per BR4-010.
 */
export function CrossModuleCountsSection() {
	const d = useDashboard()
	if (!d.isSuperAdminViewer) return null

	const people: CountTileSpec[] = [
		{ label: 'Employees', href: '/employees', query: d.employeesCountQuery, tone: 'bg-blue-50 text-blue-700' },
		{ label: 'Contractors', href: '/contractors', query: d.contractorsCountQuery, tone: 'bg-teal-50 text-teal-700' },
		{
			label: 'Entra Directory',
			href: '/entra-directory',
			query: d.entraDirectoryCountQuery,
			tone: 'bg-cyan-50 text-cyan-700',
		},
	]
	const organization: CountTileSpec[] = [
		{ label: 'Clients', href: '/clients', query: d.clientsCountQuery, tone: 'bg-amber-50 text-amber-700' },
		{ label: 'Projects', href: '/projects', query: d.projectsCountQuery, tone: 'bg-orange-50 text-orange-700' },
		{ label: 'Providers', href: '/providers', query: d.providersCountQuery, tone: 'bg-lime-50 text-lime-700' },
	]
	const accessSecurity: CountTileSpec[] = [
		{
			label: 'IDM Requests',
			href: '/idm-requests',
			query: d.idmRequestsCountQuery,
			tone: 'bg-indigo-50 text-indigo-700',
		},
		{
			label: 'ACC Mappings',
			href: '/idm-acc-mappings',
			query: d.accMappingsCountQuery,
			tone: 'bg-violet-50 text-violet-700',
		},
		{
			label: 'Permissions',
			href: '/permissions',
			query: d.permissionsCountQuery,
			tone: 'bg-purple-50 text-purple-700',
		},
	]

	return (
		<div className="space-y-6">
			<TileGroup headingId="people-heading" heading="People" tiles={people} />
			<TileGroup headingId="organization-heading" heading="Organization" tiles={organization} />
			<TileGroup headingId="access-security-heading" heading={<>Access &amp; Security</>} tiles={accessSecurity} />
		</div>
	)
}
